import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { MobileCourseLayout } from "@/components/layout/MobileCourseLayout";
import { DesktopCourseLayout } from "@/components/layout/DesktopCourseLayout";
import { LoadingState } from "@/components/common/LoadingState";
import { ErrorState } from "@/components/common/ErrorState";
import { Sheet } from "@/components/ui/sheet";
import { GlossarySlideover } from "@/components/glossary/GlossarySlideover";
import { screen, useMediaQuery } from "@/hooks/useMediaQuery";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseGlossaryStore } from "@/stores/courseGlossaryStore";

export const CourseDetailPage = () => {
  const { enrollmentId, courseExternalId } = useParams<{
    enrollmentId?: string;
    courseExternalId?: string;
  }>();
  const {
    courseData,
    loading,
    error,
    fetchCourse,
    fetchCourseByExternalId,
    fetchTrainerPreviewCourse,
  } = useCourseDetailStore();
  const isMobile = !useMediaQuery(screen.md);
  const { pathname } = useLocation();
  const isTrainerPreview = pathname.includes("/trainer-preview");
  // The glossary opens from the header, which is up here with the whole
  // course. Mounted on the page rather than inside the content pane: that
  // pane returns early for intro, outro and chapter views, and the slideover
  // would be missing exactly there.
  const courseId = useCourseDetailStore((state) => state.courseStructureId);
  const glossaryOpen = useCourseGlossaryStore((state) => state.isOpen);
  const setGlossaryOpen = useCourseGlossaryStore(
    (state) => state.setGlossaryOpen
  );

  useEffect(() => {
    if (courseExternalId) {
      fetchCourseByExternalId(courseExternalId);
    } else if (enrollmentId && isTrainerPreview) {
      fetchTrainerPreviewCourse(enrollmentId);
    } else if (enrollmentId) {
      fetchCourse(enrollmentId);
    }
  }, [
    enrollmentId,
    courseExternalId,
    isTrainerPreview,
    fetchCourse,
    fetchCourseByExternalId,
    fetchTrainerPreviewCourse,
  ]);

  if (!enrollmentId && !courseExternalId) {
    return (
      <ErrorState
        title="Einschreibung nicht gefunden"
        message="Keine gültige Einschreibungs-ID in der URL"
      />
    );
  }

  if (loading) {
    return <LoadingState message="Kurs wird geladen" />;
  }

  if (error || !courseData) {
    if (error === "missing_permissions") {
      return (
        <ErrorState
          title="Keine Berechtigung"
          message="Sie benötigen Administratorrechte, um eine Kursvorschau anzuzeigen."
        />
      );
    }
    return (
      <ErrorState
        title="Das Laden des Kurses ist fehlgeschlagen"
        message={error || undefined}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 overflow-hidden p-0">
      {isMobile ? <MobileCourseLayout /> : <DesktopCourseLayout />}

      {courseId && (
        <Sheet open={glossaryOpen} onOpenChange={setGlossaryOpen}>
          <GlossarySlideover courseId={courseId} />
        </Sheet>
      )}
    </div>
  );
};
